import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setPropertyValue,
  setInterestRate,
  setTimePeriod,
  setPropertyType,
  setEmploymentType,
  setMonthlyEmi,
  resetLtvData,
  setCalculationResults,
} from "../redux/calculations/ltvSlice";
import {
  resetChartData,
  updateLtvChart,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const LtvCalc = () => {
  const dispatch = useDispatch();
  const {
    propertyValue,
    interestRate,
    timePeriod,
    propertyType,
    employmentType,
  } = useSelector((state) => state.ltvCalculator);
  const { textData } = useLocale();
  const { ltvCalc } = textData;
  const [formattedPropertyValue, setFormattedPropertyValue] = useState("");
  const [selectedOption, setSelectedOption] = useState("salaried");
  const [calcDone, setCalcDone] = useState(false);
  const [showTable, setShowTable] = useState(false);

  const [selectedProOption, setSelectedProOption] = useState("residential");
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const { t } = useTranslation();
  const [isFormModified, setIsFormModified] = useState(false);

  const formik = useFormik({
    initialValues: {
      propertyValue: propertyValue,
      interestRate: interestRate,
      timePeriod: timePeriod,
      propertyType: propertyType,
      employmentType: employmentType,
    },
    validationSchema: Yup.object({
      propertyValue: Yup.number()
        .min(100000, ltvCalc.propertyValue)
        .max(30000000, ltvCalc.propertyValue)
        .required(ltvCalc.propertyRequired),
      interestRate: Yup.number()
        .min(6.5, ltvCalc.interestRate)
        .max(16, ltvCalc.interestRate)
        .required(ltvCalc.intrestRequired),
      timePeriod: Yup.number()
        .min(1, ltvCalc.timePeriod)
        .max(30, ltvCalc.timePeriod)
        .required(ltvCalc.timeRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_ltvCalc");
      }
      const principal = parseFloat(values.propertyValue);
      const rate = parseFloat(values.interestRate) / 1200;
      const time = parseFloat(values.timePeriod) * 12;
      let ltvRatio = 0.8;
      if (values.propertyType === "residential") {
        ltvRatio = values.employmentType === "salaried" ? 0.75 : 0.7;
      } else {
        ltvRatio = values.employmentType === "salaried" ? 0.7 : 0.65;
      }

      const loanAmount = principal * ltvRatio;
      const emi =
        (loanAmount * rate * Math.pow(1 + rate, time)) /
        (Math.pow(1 + rate, time) - 1);
      const totalPayment = emi * time;
      const totalInterest = totalPayment - loanAmount;
      const principalAmount = totalPayment - totalInterest;
      dispatch(setMonthlyEmi(emi));
      dispatch(
        updateLtvChart({
          principalAmount: principalAmount,
          interestAmount: totalInterest,
          totalAmount: totalPayment,
          monthlyEmi: emi,
        })
      );

      dispatch(setCalculationResults({
        totalInvestment: principal,
        interesttRate: rate,
        time: time,
        yearlyReturn: totalInterest,
        interestRate: values.interestRate,
        tenure: values.timePeriod,
        emi: emi,
        propertyType: values.propertyType,
        employmentType: values.employmentType,
        maturityAmount: totalPayment,
      }));

      saveToHistory({
        tabName: "loanToValueCalc",
        totalInvestment: principal,
        interesttRate: rate,
        time: time,
        yearlyReturn: totalInterest,
        interestRate: values.interestRate,
        tenure: values.timePeriod,
        emi: emi,
        propertyType: values.propertyType,
        employmentType: values.employmentType,
        maturityAmount: totalPayment,
        calculationType: "Ltv",
      });
      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetLtvData());
    };
  }, [dispatch]);
  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];

      const newHistoryRecord = {
        tabName: calculationData.tabName,
        interesttRate: calculationData.interesttRate,
        time: calculationData.time,
        totalInvestment: calculationData.totalInvestment,
        yearlyReturn: calculationData.yearlyReturn,
        interestRate: calculationData.interestRate,
        tenure: calculationData.tenure,
        emi: calculationData.emi,
        propertyType: calculationData.propertyType,
        employmentType: calculationData.employmentType,
        maturityAmount: calculationData.maturityAmount,
        calculationType: calculationData.calculationType,
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.totalInvestment === newHistoryRecord.totalInvestment &&
          record.interestRate === newHistoryRecord.interestRate &&
          record.tenure === newHistoryRecord.tenure &&
          record.maturityAmount === newHistoryRecord.maturityAmount &&
          record.emi === newHistoryRecord.emi &&
          record.propertyType === newHistoryRecord.propertyType &&
          record.employmentType === newHistoryRecord.employmentType
        );
      });
      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) { }
  };

  useEffect(() => {
    setFormattedPropertyValue(formatToINR(formik.values.propertyValue));
  }, [formik.values.propertyValue]);

  const propertyValueSliderRef = useRef(null);
  const interestSliderRef = useRef(null);
  const periodSliderRef = useRef(null);

  const handlePropertyTypeChange = (type) => {
    formik.setFieldValue("propertyType", type);
    setSelectedProOption(type);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleEmploymentTypeChange = (type) => {
    formik.setFieldValue("employmentType", type);
    setSelectedOption(type);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleTimePeriodSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setTimePeriod(value));
    formik.setFieldValue("timePeriod", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInterestSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setInterestRate(value));
    formik.setFieldValue("interestRate", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handlePropertyValueSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setPropertyValue(value));
    formik.setFieldValue("propertyValue", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  // Format to Indian Rupee format
  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };

  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue.length > 2) return;
    formik.setFieldValue("timePeriod", rawValue);
    if (rawValue === "") return;
    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      const clamped = Math.max(1, Math.min(30, numericValue));
      dispatch(setTimePeriod(clamped));
      setIsFormModified(true);
    }
    setCalcDone(false);
  };
  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("interestRate", rawValue);
      dispatch(setInterestRate(""));

      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;

    const normalizedValue = DecimalUtils.normalizeInput(rawValue);
    formik.setFieldValue("interestRate", normalizedValue);
    dispatch(setInterestRate(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };
  const handlePropertyValueChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, "");

    const numericValue = rawValue === "" ? "" : parseInt(rawValue, 10);
    if (rawValue.length > 9) return;

    setFormattedPropertyValue(formatToINR(numericValue));

    formik.setFieldValue("propertyValue", numericValue);
    dispatch(setPropertyValue(numericValue));
    setIsFormModified(true);
    setCalcDone(false);
  };
  useEffect(() => {
    dispatch(setPropertyValue(formik.values.propertyValue));
    dispatch(setInterestRate(formik.values.interestRate));
    dispatch(setTimePeriod(formik.values.timePeriod));
    dispatch(setPropertyType(formik.values.propertyType));
    dispatch(setEmploymentType(formik.values.employmentType));
  }, [formik.values, dispatch]);
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.ltvContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.ltvContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.ltvContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.ltvContent.howItWorksDescription}
      </p>
      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        {textData.ltvContent.ltvFormula}
      </p>

      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.ltvContent.ltvFormulaDescription}
      </p>
      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        {textData.ltvContent.emiFormula}
      </p>

      <table className="border-collapse mt-4 w-full">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-3 border border-gray-light w-1/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.ltvContent.symbolColumn}
            </th>
            <th className="p-3 border border-gray-light w-2/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.ltvContent.descriptionColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              P
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.ltvContent.symbolsTable.descriptionP}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              r
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.ltvContent.symbolsTable.descriptionM}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              n
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.ltvContent.symbolsTable.descriptionL}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.ltvContent.howToUseTitle}
      </h2>
      <ol className="list-decimal px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li> {textData.ltvContent.howToUseSteps[0]}</li>
        <li> {textData.ltvContent.howToUseSteps[1]}</li>
        <li> {textData.ltvContent.howToUseSteps[2]}</li>

        <li> {textData.ltvContent.howToUseSteps[3]}</li>
        <li> {textData.ltvContent.howToUseSteps[4]}</li>
        <li> {textData.ltvContent.howToUseSteps[5]}</li>
      </ol>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.ltvContent.factorsAffectingLtvTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong> {textData.ltvContent.factorsAffectingLtv[0].factor}:</strong>{" "}
          {textData.ltvContent.factorsAffectingLtv[0].description}
        </li>
        <li>
          <strong> {textData.ltvContent.factorsAffectingLtv[1].factor}:</strong>{" "}
          {textData.ltvContent.factorsAffectingLtv[1].description}
        </li>
        <li>
          <strong> {textData.ltvContent.factorsAffectingLtv[2].factor}:</strong>{" "}
          {textData.ltvContent.factorsAffectingLtv[2].description}
        </li>
        <li>
          <strong> {textData.ltvContent.factorsAffectingLtv[3].factor}:</strong>{" "}
          {textData.ltvContent.factorsAffectingLtv[3].description}
        </li>
        <li>
          <strong> {textData.ltvContent.factorsAffectingLtv[4].factor}:</strong>{" "}
          {textData.ltvContent.factorsAffectingLtv[4].description}
        </li>
        <li>
          <strong> {textData.ltvContent.factorsAffectingLtv[5].factor}:</strong>{" "}
          {textData.ltvContent.factorsAffectingLtv[5].description}
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.ltvContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.ltvContent.benefitsList[0]}</li>
        <li>{textData.ltvContent.benefitsList[1]}</li>
        <li>{textData.ltvContent.benefitsList[2]}</li>
        <li>{textData.ltvContent.benefitsList[3]}</li>
        <li>{textData.ltvContent.benefitsList[4]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {textData.ltvContent.tipTitle}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.ltvContent.tipContent}
      </p>
    </div>
  );
  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };
  const timeLabel = t("yearShort");

  return (
    <>
      <Helmet>
        <title>Loan to Value Calculator Online | Check Your LTV Ratio</title>
        <meta
          name="description"
          content="Check your LTV ratio instantly with our Loan to Value Calculator online. Get quick insights, plan loans, and make confident financial decisions in minutes."
        />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/loan-to-value-calculator"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Loan to Value Calculator Online | Check Your LTV Ratio"
        />
        <meta
          property="og:description"
          content="Check your LTV ratio instantly with our Loan to Value Calculator online. Get quick insights, plan loans, and make confident financial decisions in minutes."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/loan-to-value-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Loan to Value Calculator Online - GrowLifeFund"
        />

        {/* Robots */}
        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        title={
          <>
            {textData.ltvCalc.title}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.ltvCalc.ratio}
            </span>
          </>
        }
        description={textData.ltvCalc.description}
        descriptionContent={descriptionContent}
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        setCalcDone={setCalcDone}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div >
              <div className="flex gap-4 sm:gap-8 mb-4 justify-between sm:justify-normal">
                <div className="flex items-center">
                  <label
                    htmlFor="salaried"
                    className="gap-[7px] sm:gap-[10px] text-black cursor-pointer sm:text-sm items-center text-[14px] flex"
                  >
                    <input
                      type="radio"
                      id="salaried"
                      name="employmentType"
                      checked={selectedOption === "salaried"}
                      onChange={() => handleEmploymentTypeChange("salaried")}
                      className="sr-only"
                    />
                    <div
                      className={`h-6 w-6 rounded-full border ${selectedOption === "salaried"
                        ? "border-[#07B5B5] bg-white"
                        : "border-[#07B5B5]"
                        } flex items-center justify-center`}
                    >
                      {selectedOption === "salaried" && (
                        <div className="h-3 w-3 rounded-full bg-[#07B5B5]"></div>
                      )}
                    </div>

                    <span>{textData.ltvCalc.salariedLabel}</span>
                  </label>
                </div>
                <div className="flex items-center">
                  <label
                    htmlFor="selfEmployed"
                    className="gap-[7px] sm:gap-[10px] text-black cursor-pointer items-center sm:text-sm text-[14px] flex"
                  >
                    <input
                      type="radio"
                      id="selfEmployed"
                      name="employmentType"
                      checked={selectedOption === "selfEmployed"}
                      onChange={() =>
                        handleEmploymentTypeChange("selfEmployed")
                      }
                      className="sr-only"
                    />
                    <div
                      className={`h-6 w-6 rounded-full border ${selectedOption === "salaried"
                        ? "border-[#07B5B5] bg-white"
                        : "border-[#07B5B5]"
                        } flex items-center justify-center`}
                    >
                      {selectedOption === "selfEmployed" && (
                        <div className="h-3 w-3 rounded-full bg-[#07B5B5]"></div>
                      )}
                    </div>

                    <span> {textData.ltvCalc.selfEmployedLabel}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Property Value */}
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.ltvCalc.propertyValueLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={propertyValueSliderRef}
                >
                  
                  <input
                    type="range"
                    min={100000}
                    max={30000000}
                    step={10000}
                    value={formik.values.propertyValue}
                    onChange={handlePropertyValueSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.propertyValue - 100000) /
                        (30000000 - 100000)) *
                        100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="propertyValue"
                    leftIcon={textData.currency}
                    value={formattedPropertyValue}
                    onChange={handlePropertyValueChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.propertyValue &&
                        formik.errors.propertyValue
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]   ${formik.touched.propertyValue &&
                      formik.errors.propertyValue
                      ? "border-danger"
                      : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.propertyValue &&
                    formik.errors.propertyValue && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.propertyValue}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Property Type Selection */}
            <div >
              <div className="flex  gap-1 sm:gap-8  mb-4 ">
                <div className="flex items-center">
                  <label
                    htmlFor="residential"
                    className="gap-[7px] sm:gap-[10px] flex items-center cursor-pointer "
                  >
                    <input
                      type="radio"
                      id="residential"
                      name="propertyType"
                      checked={selectedProOption === "residential"}
                      onChange={() => handlePropertyTypeChange("residential")}
                      className="sr-only"
                    />
                    <div
                      className={`h-6 w-6 rounded-full border ${selectedProOption === "residential"
                        ? "border-[#07B5B5] bg-white"
                        : "border-[#07B5B5]"
                        } flex items-center justify-center shrink-0`}
                    >
                      {selectedProOption === "residential" && (
                        <div className="h-3 w-3 rounded-full bg-[#07B5B5]"></div>
                      )}
                    </div>
                    <span className=" text-black sm:text-sm text-[14px]">
                      {textData.ltvCalc.residentialLabel}
                    </span>
                  </label>
                </div>
                <div className="flex items-center">
                  <label
                    htmlFor="commercial"
                    className="gap-[7px] sm:gap-[10px] text-black cursor-pointer items-center sm:text-sm text-[14px] flex"
                  >
                    <input
                      type="radio"
                      id="commercial"
                      name="propertyType"
                      checked={selectedProOption === "commercial"}
                      onChange={() => handlePropertyTypeChange("commercial")}
                      className="sr-only"
                    />
                    <div
                      className={`h-6 w-6 rounded-full border ${selectedProOption === "residential"
                        ? "border-[#07B5B5] bg-white"
                        : "border-[#07B5B5]"
                        } flex items-center justify-center shrink-0`}
                    >
                      {selectedProOption === "commercial" && (
                        <div className="h-3 w-3 rounded-full bg-[#07B5B5]"></div>
                      )}
                    </div>
                    <span> {textData.ltvCalc.commercialLabel}</span>{" "}
                  </label>
                </div>
              </div>
            </div>

            {/* Interest Rate */}
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.rateOfInterest}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={interestSliderRef}>
                  
                  <input
                    type="range"
                    min={6.5}
                    max={16}
                    step={0.1}
                    value={parseFloat(interestRate)}
                    onChange={handleInterestSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((interestRate - 6.5) / (16 - 6.5)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="interestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("12.0")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.interestRate
                    )}
                    onChange={handleInterestRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.interestRate && formik.errors.interestRate
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]   ${formik.touched.interestRate && formik.errors.interestRate
                      ? "border-danger"
                      : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.interestRate &&
                    formik.errors.interestRate && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.interestRate}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Time Period */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.timePeriod}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={periodSliderRef}>
                  
                  <input
                    type="range"
                    min={1}
                    max={30}
                    step={1}
                    value={formik.values.timePeriod}
                    onChange={handleTimePeriodSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.timePeriod - 1) / (30 - 1)) * 100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="timePeriod"
                    leftIcon={timeLabel}
                    value={formik.values.timePeriod}
                    onChange={handleTimeChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"5"}
                    borderColor={
                      formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]   ${formik.touched.timePeriod && formik.errors.timePeriod
                      ? "border-danger"
                      : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.timePeriod && formik.errors.timePeriod && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.timePeriod}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Calculate Button */}
            <div className="mt-[10px]">
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default LtvCalc;
